import { useState, useEffect } from 'react';

export interface MediaFiltersProps {
  availableTags?: Array<{ tag: string; count: number }>;
  selectedTags?: string[];
  onTagsChange?: (tags: string[]) => void;
  publicationFilter?: 'all' | 'published' | 'unpublished';
  onPublicationFilterChange?: (filter: 'all' | 'published' | 'unpublished') => void;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  showSharedMedia?: boolean;
  onShowSharedMediaChange?: (show: boolean) => void;
  className?: string;
}

export function MediaFilters({
  availableTags = [],
  selectedTags = [],
  onTagsChange,
  publicationFilter = 'all',
  onPublicationFilterChange,
  searchQuery = '',
  onSearchChange,
  showSharedMedia = false,
  onShowSharedMediaChange,
  className = '',
}: MediaFiltersProps) {
  const [isTagsExpanded, setIsTagsExpanded] = useState(false);
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (onSearchChange && localSearchQuery !== searchQuery) {
        onSearchChange(localSearchQuery);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [localSearchQuery, onSearchChange, searchQuery]);

  const handleTagToggle = (tag: string) => {
    if (!onTagsChange) return;

    const newTags = selectedTags.includes(tag)
      ? selectedTags.filter(t => t !== tag)
      : [...selectedTags, tag];
    
    onTagsChange(newTags);
  };

  const handleClearAllTags = () => {
    if (onTagsChange) {
      onTagsChange([]);
    }
  };

  const handleClearAllFilters = () => {
    if (onTagsChange) onTagsChange([]);
    if (onPublicationFilterChange) onPublicationFilterChange('all');
    if (onSearchChange) onSearchChange('');
    if (onShowSharedMediaChange) onShowSharedMediaChange(false);
    setLocalSearchQuery('');
  };

  const hasActiveFilters = 
    selectedTags.length > 0 || 
    publicationFilter !== 'all' || 
    searchQuery.length > 0 ||
    showSharedMedia;

  const displayedTags = isTagsExpanded ? availableTags : availableTags.slice(0, 8);

  return (
    <div className={`bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4 ${className}`}>
      {/* Search */}
      <div>
        <label htmlFor="media-search" className="block text-sm font-medium text-gray-700 mb-2">
          Search Media
        </label>
        <div className="relative">
          <input
            id="media-search"
            type="text"
            value={localSearchQuery}
            onChange={(e) => setLocalSearchQuery(e.target.value)}
            placeholder="Search by filename or caption..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-info-500 focus:border-info-500"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg
              className="h-5 w-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Publication Status Filter */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Publication Status
        </label>
        <div className="flex space-x-2">
          {[
            { value: 'all', label: 'All' },
            { value: 'published', label: 'Published' },
            { value: 'unpublished', label: 'Unpublished' },
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => onPublicationFilterChange?.(option.value as 'all' | 'published' | 'unpublished')}
              className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                publicationFilter === option.value
                  ? 'bg-primary-100 border-primary-300 text-primary-800'
                  : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Shared Media Toggle */}
      <div>
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={showSharedMedia}
            onChange={(e) => onShowSharedMediaChange?.(e.target.checked)}
            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-info-500"
          />
          <span className="text-sm font-medium text-gray-700">
            Include shared media
          </span>
        </label>
        <p className="text-xs text-gray-500 mt-1">
          Show stock photos available to all venues
        </p>
      </div>

      {/* Tags Filter */}
      {availableTags.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Filter by Tags
            </label>
            {selectedTags.length > 0 && (
              <button
                onClick={handleClearAllTags}
                className="text-xs text-primary-600 hover:text-primary-700"
              >
                Clear all
              </button>
            )}
          </div>
          
          <div className="flex flex-wrap gap-2">
            {displayedTags.map(({ tag, count }) => (
              <button
                key={tag}
                onClick={() => handleTagToggle(tag)}
                className={`inline-flex items-center px-3 py-1 text-sm rounded-full border transition-colors ${
                  selectedTags.includes(tag)
                    ? 'bg-primary-100 border-primary-300 text-primary-800'
                    : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {tag}
                <span className="ml-1 text-xs opacity-75">({count})</span>
              </button>
            ))}
          </div>

          {availableTags.length > 8 && (
            <button
              onClick={() => setIsTagsExpanded(!isTagsExpanded)}
              className="mt-2 text-sm text-primary-600 hover:text-primary-700"
            >
              {isTagsExpanded ? 'Show less' : `Show ${availableTags.length - 8} more tags`}
            </button>
          )}
        </div>
      )}

      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <div className="pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">
              {selectedTags.length > 0 && `${selectedTags.length} tag${selectedTags.length > 1 ? 's' : ''}`}
              {selectedTags.length > 0 && publicationFilter !== 'all' && ', '}
              {publicationFilter !== 'all' && `${publicationFilter} only`}
              {(selectedTags.length > 0 || publicationFilter !== 'all') && searchQuery && ', '}
              {searchQuery && `search: "${searchQuery}"`}
              {showSharedMedia && ', including shared'}
            </span>
            <button
              onClick={handleClearAllFilters}
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              Clear all filters
            </button>
          </div>
        </div>
      )}
    </div>
  );
}